import React, { useCallback, useMemo } from 'react';
import { useFetchedPromptVariables } from '../hooks/useFetchedPromptVariables';
import { PromptVariable, PromptVariableViewModel, VARIABLE_METADATAS } from '../../types';
import { ItemListAndDetails } from '../../../commons/components/ItemListAndDetails';
import { getDefaultVariableValue, isPromptVariableFilled } from '../../util';
import { VariableList } from '../components/VariableList';
import _ from 'lodash';
import { VariableBox } from '../components/VariableBox';
import { CHAT_TYPE_GENERAL } from '../../dto';

export const VariablesTab = (props: {
  selectedVariable: PromptVariable | null;
  onVariableSelected: (promptVariable: PromptVariable | null) => void;
}) => {
  const { selectedVariable, onVariableSelected } = props;
  const {
    query: { data: variables },
    queryStatusComponent: variablesQueryStatusComponent,
  } = useFetchedPromptVariables();

  const variableViewModels: PromptVariableViewModel[] | null = useMemo(
    () =>
      variables
        ? VARIABLE_METADATAS.filter((m) => !m.appliesTo || m.appliesTo === CHAT_TYPE_GENERAL)
            .map((m, index) => {
              const promptVariable: PromptVariable = (variables ?? []).find(
                (x) => x.variableValue.name === m.name
              ) ?? {
                id: null,
                isPrivate: false,
                variableValue: getDefaultVariableValue(m.name),
              };
              const isSuggested = m.isSuggested && !isPromptVariableFilled(promptVariable);
              return {
                ...promptVariable,
                isSuggested,
                sortOrder: isSuggested ? index - VARIABLE_METADATAS.length : index,
                isFilled: isPromptVariableFilled(promptVariable),
                note: m.note ?? '',
              };
            })
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .concat(
              variables
                .filter((x) => !VARIABLE_METADATAS.some((m) => x.variableValue.name === m.name))
                .map(
                  (x): PromptVariableViewModel => ({
                    ...x,
                    isSuggested: false,
                    sortOrder: VARIABLE_METADATAS.length,
                    isFilled: isPromptVariableFilled(x),
                    note: '',
                  })
                )
            )
        : null,
    [variables]
  );

  const idGetter = useCallback((x: PromptVariable) => x.variableValue.name, []);
  const nameGetter = useCallback((x: PromptVariable) => _.capitalize(x.variableValue.name), []);
  return (
    <div className="growegy-ai__variables-tab-container">
      <ItemListAndDetails
        items={variableViewModels}
        itemsLoadingComponent={variablesQueryStatusComponent}
        itemListExtraProps={{}}
        itemListComponent={VariableList}
        detailsComponent={VariableBox}
        defaultSelectedItemId={selectedVariable ? idGetter(selectedVariable) : null}
        onItemSelected={onVariableSelected}
        nameGetter={nameGetter}
        idGetter={idGetter}
      />
    </div>
  );
};
